import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { TrendingUp, TrendingDown, Minus, X, Maximize2 } from 'lucide-react';
import Logo from '../components/Logo';

const fmtMoney = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const Delta = ({ value }) => {
  if (value === null || value === undefined) return <span className="text-xs" style={{ color: 'rgba(245,241,232,0.35)' }}>—</span>;
  if (value === 0) return <span className="text-xs flex items-center gap-1" style={{ color: 'rgba(245,241,232,0.5)' }}><Minus size={11} /> flat</span>;
  const up = value > 0;
  return (
    <span className="text-xs flex items-center gap-1 font-medium" style={{ color: up ? '#86efac' : '#fda4af' }}>
      {up ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
      {up ? '+' : ''}{value}%
    </span>
  );
};

const BigStat = ({ label, value, delta, accent = '#C9A961', testid }) => (
  <div className="flex flex-col justify-between p-8 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(201,169,97,0.12)' }} data-testid={testid}>
    <div>
      <p className="text-[11px] uppercase tracking-[0.22em] mb-3" style={{ color: 'rgba(201,169,97,0.7)' }}>{label}</p>
      <p className="font-heading font-light leading-none" style={{ color: accent, fontSize: 'clamp(48px, 6vw, 96px)' }}>{value}</p>
    </div>
    <div className="mt-4"><Delta value={delta} /> <span className="text-[10px] uppercase tracking-widest ml-2" style={{ color: 'rgba(245,241,232,0.35)' }}>vs prior week</span></div>
  </div>
);

export default function HuddleDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const fetchData = async () => {
    try {
      const r = await axios.get(`${API}/huddle/weekly`);
      setData(r.data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, 60000); // refresh every minute
    return () => clearInterval(id);
  }, []);

  const goFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.();
      setFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setFullscreen(false);
    }
  };

  if (!user) return null;

  if (loading || !data) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: '#0E0E0E', color: '#C9A961' }} data-testid="huddle-loading">Loading huddle…</div>;
  }

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: '#0E0E0E' }} data-testid="huddle-page">
      {/* Background ambience */}
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 20% 0%, rgba(201,169,97,0.06), transparent 55%)' }} />

      {/* Header */}
      <div className="relative z-10 flex items-center justify-between px-12 py-8 border-b" style={{ borderColor: 'rgba(201,169,97,0.12)' }}>
        <Logo size={44} light showText />
        <div className="text-center">
          <p className="text-[11px] uppercase tracking-[0.32em]" style={{ color: 'rgba(201,169,97,0.7)' }}>Weekly Huddle</p>
          <p className="font-heading text-2xl mt-1" style={{ color: '#F5F1E8' }}>
            {data.week_start} <span style={{ color: 'rgba(201,169,97,0.5)' }}>→</span> {data.week_end}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={goFullscreen} className="p-2 rounded hover:bg-white/5" style={{ color: 'rgba(245,241,232,0.5)' }} data-testid="huddle-fullscreen-btn"><Maximize2 size={18} /></button>
          <button onClick={() => navigate('/')} className="p-2 rounded hover:bg-white/5" style={{ color: 'rgba(245,241,232,0.5)' }} data-testid="huddle-exit-btn"><X size={18} /></button>
        </div>
      </div>

      {/* Big stats grid */}
      <div className="relative z-10 px-12 py-10 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        <BigStat label="Arches Sold" value={data.current.arches} delta={data.deltas.arches} testid="huddle-arches" />
        <BigStat label="FA Presented" value={data.current.fa_presented} delta={data.deltas.fa_presented} testid="huddle-fa-presented" />
        <BigStat label="FA Close Rate" value={`${data.current.close_rate}%`} delta={data.deltas.close_rate} testid="huddle-close-rate" />
        <BigStat label="Collections" value={fmtMoney(data.current.collections)} delta={data.deltas.collections} testid="huddle-collections" />
      </div>

      <div className="relative z-10 px-12 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        <BigStat label="Leads" value={data.current.leads} delta={data.deltas.leads} accent="#F5F1E8" testid="huddle-leads" />
        <BigStat label="Bookings" value={data.current.bookings} delta={data.deltas.bookings} accent="#F5F1E8" testid="huddle-bookings" />
        <BigStat label="Consults Showed" value={data.current.consults_showed} delta={data.deltas.consults_showed} accent="#F5F1E8" testid="huddle-shows" />
        <BigStat label="Avg Case Value" value={fmtMoney(data.current.avg_case_value)} delta={data.deltas.avg_case_value} accent="#F5F1E8" testid="huddle-avg-case" />
      </div>

      {/* Per-location strip & top referral */}
      <div className="relative z-10 px-12 py-10 mt-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 p-8 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(201,169,97,0.12)' }}>
            <p className="text-[11px] uppercase tracking-[0.22em] mb-5" style={{ color: 'rgba(201,169,97,0.7)' }}>By Location</p>
            {data.per_location.length === 0 ? (
              <p className="text-sm" style={{ color: 'rgba(245,241,232,0.5)' }}>No locations</p>
            ) : (
              <div className="space-y-4">
                {data.per_location.map((loc) => (
                  <div key={loc.location_id} className="flex items-center justify-between pb-4 last:pb-0 border-b last:border-0" style={{ borderColor: 'rgba(201,169,97,0.08)' }}>
                    <div>
                      <p className="font-heading text-lg" style={{ color: '#F5F1E8' }}>{loc.name}</p>
                      <p className="text-xs mt-0.5" style={{ color: 'rgba(245,241,232,0.4)' }}>{loc.fa_presented} presented · {loc.close_rate}% close · {fmtMoney(loc.collections)} collected</p>
                    </div>
                    <p className="font-heading text-3xl" style={{ color: '#C9A961' }}>{loc.arches}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="p-8 rounded-lg flex flex-col justify-between" style={{ background: 'rgba(201,169,97,0.06)', border: '1px solid rgba(201,169,97,0.25)' }}>
            <div>
              <p className="text-[11px] uppercase tracking-[0.22em] mb-3" style={{ color: 'rgba(201,169,97,0.85)' }}>Top Referral Source</p>
              <p className="font-heading text-3xl italic" style={{ color: '#F5F1E8' }}>{data.top_referral || 'No data'}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.22em] mb-2" style={{ color: 'rgba(201,169,97,0.85)' }}>Revenue</p>
              <p className="font-heading text-4xl" style={{ color: '#C9A961' }}>{fmtMoney(data.current.revenue)}</p>
              <Delta value={data.deltas.revenue} />
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-10 text-center pb-8 text-[10px] uppercase tracking-[0.32em]" style={{ color: 'rgba(245,241,232,0.25)' }}>
        Auto-refreshes every minute · Press F11 or click ⤢ for full-screen
      </div>
    </div>
  );
}
